import { observer } from "mobx-react-lite";
import {
  type FC,
  type JSX,
  type ReactElement,
  useCallback,
  useId,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { AdminSections } from "@/api/admin/teamRoles";
import {
  CustomBadge,
  CustomButton,
  CustomCardFooter,
  CustomCardHeader,
  FormCardBasic,
  HeaderWithBack,
  Loader,
} from "@/components/atoms";
import { FormNavigation } from "@/components/molecules";
import {
  BranchDetailsForm,
  type BranchDetailsType,
  BranchHoursForm,
  type BranchHoursFormType,
  BranchMediaForm,
  type BranchMediaFormType,
  BranchReservationForm,
  type BranchReservationType,
  type BrandAmenitiesType,
  BrandFormAmenities,
} from "@/components/units";
import { PATHS } from "@/config/path";
import type { BranchRouter } from "@/modules/BranchStepsAdmin/branchRouter";
import type { DemoBranchStore } from "@/modules/BranchStepsAdmin/DemoBranchStore";
import { useBranchContext } from "@/modules/BranchStepsAdmin/hooks";
import {
  clearWizardDraft,
  NEW_WIZARD_DRAFT_ENTITY_ID,
} from "@/modules/wizardDraft";
import { usePermission } from "@/services/casl/usePermission";
import { rootStore } from "@/store/RootStore";
import type { FormRefType } from "@/types/common";

import { useBranchNavigation } from "./useBranchNavigation";

interface BranchStepsProps {
  branchStore: DemoBranchStore;
  router: BranchRouter;
  header?: ReactElement;
  isPending?: boolean;
  onSubmit?: () => Promise<boolean | string>;
}

const FIRST_STEP = 1;
const LAST_STEP = 5;

const BranchSteps: FC<BranchStepsProps> = observer(
  ({ branchStore, router, header, onSubmit }) => {
    const navigate = useNavigate();
    const isPending = branchStore.isCreating || branchStore.isUpdating;

    if (!branchStore || !router) {
      throw new Error(
        "BranchSteps requires either BranchWizardContext or props (branchStore, router)"
      );
    }

    const idForm = useId();
    // Track validity per step to avoid conflicts when multiple forms are rendered
    const [stepValidity, setStepValidity] = useState<Record<number, boolean>>({
      1: false,
      2: false,
      3: false,
      4: false,
      5: false,
    });

    const isAdmin = rootStore.authStore.isAdmin;

    const detailsRef = useRef<FormRefType<BranchDetailsType>>(null);
    const hoursRef = useRef<FormRefType<BranchHoursFormType>>(null);
    const reservationsRef = useRef<FormRefType<BranchReservationType>>(null);
    const amenitiesRef = useRef<FormRefType<BrandAmenitiesType>>(null);
    const mediaRef = useRef<FormRefType<BranchMediaFormType>>(null);

    // Create stable validity handlers for each step using useCallback
    // This prevents infinite loops by ensuring the callbacks don't change on every render
    const handleStep1ValidityChange = useCallback((isValid: boolean) => {
      setStepValidity(prev => {
        if (prev[1] === isValid) return prev; // Prevent unnecessary updates
        return { ...prev, 1: isValid };
      });
    }, []);

    const handleStep2ValidityChange = useCallback((isValid: boolean) => {
      setStepValidity(prev => {
        if (prev[2] === isValid) return prev; // Prevent unnecessary updates
        return { ...prev, 2: isValid };
      });
    }, []);

    const handleStep3ValidityChange = useCallback((isValid: boolean) => {
      setStepValidity(prev => {
        if (prev[3] === isValid) return prev; // Prevent unnecessary updates
        return { ...prev, 3: isValid };
      });
    }, []);

    const handleStep4ValidityChange = useCallback((isValid: boolean) => {
      setStepValidity(prev => {
        if (prev[4] === isValid) return prev; // Prevent unnecessary updates
        return { ...prev, 4: isValid };
      });
    }, []);

    const handleStep5ValidityChange = useCallback((isValid: boolean) => {
      setStepValidity(prev => {
        if (prev[5] === isValid) return prev; // Prevent unnecessary updates
        return { ...prev, 5: isValid };
      });
    }, []);

    const { branchId: routeBranchId, hasFixedBrandId } = useBranchContext();
    const canChangeBrand = !hasFixedBrandId;
    const draftEntityId =
      branchStore.details?.id ?? routeBranchId ?? NEW_WIZARD_DRAFT_ENTITY_ID;

    const clearDraft = (entityId = draftEntityId) => {
      clearWizardDraft("branch", entityId);
    };

    // Render all forms but conditionally show them based on active step
    // This ensures forms maintain their state when switching steps
    const detailsForm = (
      <BranchDetailsForm
        ref={detailsRef}
        id={idForm}
        onValidityChange={handleStep1ValidityChange}
        defaultValues={branchStore.details ?? branchStore.getDefaultDetails()}
        draftEntityId={draftEntityId}
        onSubmit={data => {
          handleSubmit({ details: data });
        }}
        canChangeBrand={canChangeBrand}
        showBrandSelection={true}
        branchStore={branchStore}
        classes="px-4 md:px-8"
      />
    );

    const hoursForm = (
      <BranchHoursForm
        ref={hoursRef}
        id={idForm}
        onValidityChange={handleStep2ValidityChange}
        defaultValues={
          branchStore.businessHours ?? branchStore.getDefaultBusinessHours()
        }
        draftEntityId={draftEntityId}
        onSubmit={data => {
          handleSubmit({ businessHours: data });
        }}
        classes="px-4 md:px-8"
      />
    );

    const reservationsForm = (
      <BranchReservationForm
        ref={reservationsRef}
        id={idForm}
        onValidityChange={handleStep3ValidityChange}
        defaultValues={
          branchStore.reservations ?? branchStore.getDefaultReservations()
        }
        draftEntityId={draftEntityId}
        onSubmit={data => {
          handleSubmit({ reservations: data });
        }}
        classes="px-4 md:px-8"
      />
    );

    const amenitiesForm = (
      <BrandFormAmenities
        ref={amenitiesRef}
        id={idForm}
        onValidityChange={handleStep4ValidityChange}
        defaultValues={
          branchStore.amenities ?? branchStore.getDefaultAmenities()
        }
        draftEntityId={draftEntityId}
        onSubmit={data => {
          handleSubmit({ amenities: data });
        }}
        classes="px-4 md:px-8"
      />
    );

    const mediaForm = (
      <BranchMediaForm
        ref={mediaRef}
        id={idForm}
        onValidityChange={handleStep5ValidityChange}
        defaultValues={branchStore.media ?? branchStore.getDefaultMedia()}
        draftEntityId={draftEntityId}
        onSubmit={data => {
          handleSubmit({ media: data });
        }}
        branchStore={branchStore}
        classes="px-4 md:px-8"
      />
    );

    const getStepForm = (stepId: number): JSX.Element => {
      switch (stepId) {
        case 1:
          return detailsForm;
        case 2:
          return hoursForm;
        case 3:
          return reservationsForm;
        case 4:
          return amenitiesForm;
        case 5:
          return mediaForm;
        default:
          return <></>;
      }
    };

    const { navigationList, activeId } = useBranchNavigation(
      getStepForm,
      router
    );

    const getCurrentRef = (): React.RefObject<FormRefType<unknown>> => {
      switch (activeId) {
        case 1:
          return detailsRef as React.RefObject<FormRefType<unknown>>;
        case 2:
          return hoursRef as React.RefObject<FormRefType<unknown>>;
        case 3:
          return reservationsRef as React.RefObject<FormRefType<unknown>>;
        case 4:
          return amenitiesRef as React.RefObject<FormRefType<unknown>>;
        case 5:
          return mediaRef as React.RefObject<FormRefType<unknown>>;
        default:
          return null as unknown as React.RefObject<FormRefType<unknown>>;
      }
    };

    const handleBack = () => {
      if (activeId === FIRST_STEP) {
        clearDraft();
      }
      router.toPrevious();
    };

    const handleSkip = () => {
      const currentRef = getCurrentRef();
      const formData = currentRef?.current?.getValues?.();
      if (!formData) {
        return;
      }

      const currentStepValid = stepValidity[activeId] ?? false;
      if (currentStepValid) {
        switch (activeId) {
          case 2:
            handleSubmit(
              { businessHours: formData as BranchHoursFormType },
              true
            );
            break;
          case 3:
            handleSubmit(
              {
                reservations: formData as BranchReservationType,
              },
              true
            );
            break;
          case 4:
            handleSubmit(
              {
                amenities: formData as BrandAmenitiesType,
              },
              true
            );
            break;
        }
      } else {
        switch (activeId) {
          case 2:
            branchStore.active = false;
            handleSubmit(
              { businessHours: formData as BranchHoursFormType },
              true
            );
            break;
          case 3:
            branchStore.active = false;
            handleSubmit(
              {
                reservations: formData as BranchReservationType,
              },
              true
            );
            break;
          case 4:
            branchStore.active = false;
            handleSubmit(
              {
                amenities: formData as BrandAmenitiesType,
              },
              true
            );
            break;
        }
      }
    };

    const hasReadPermission = usePermission(AdminSections.BRANCH);
    const isReadOnlyFlow = !hasReadPermission;
    const isSingleStepSave = isReadOnlyFlow && activeId === FIRST_STEP;

    // Wait for initial fetch so forms mount with loaded data (RHF defaultValues are init-only)
    const isInitialLoading = branchStore.isFetching && !branchStore.details?.id;

    const handleSubmit = async (
      data: {
        details?: BranchDetailsType;
        businessHours?: BranchHoursFormType;
        reservations?: BranchReservationType;
        amenities?: BrandAmenitiesType;
        media?: BranchMediaFormType;
      },
      skipValidation = false
    ) => {
      branchStore.setAll(data);

      const currentStepValid = stepValidity[activeId] ?? false;

      // Don't proceed if the current step is invalid
      if (!currentStepValid && !skipValidation) {
        return;
      }

      let submittedEntityId = draftEntityId;
      if (onSubmit) {
        const result = await onSubmit();
        if (!result) {
          return;
        }
        if (typeof result === "string") {
          submittedEntityId = result;
        }
      }

      if (!hasReadPermission) {
        clearDraft(submittedEntityId);
        navigate(`${PATHS.ADMIN.ACCOUNT}#branches`);
        return;
      }

      const finished = activeId === LAST_STEP;
      if (!finished) {
        router.toNext();
      } else {
        clearDraft(submittedEntityId);
        router.close();
      }
    };

    if (isInitialLoading) {
      return (
        <FormCardBasic isModal={!isAdmin}>
          <Loader />
        </FormCardBasic>
      );
    }

    return (
      <FormCardBasic isModal={!isAdmin}>
        {header ? (
          <CustomCardHeader classes="flex-row justify-between">
            {header}
          </CustomCardHeader>
        ) : (
          <HeaderWithBack
            linkProps={{
              to: "#",
              onClick: (e: React.MouseEvent) => {
                e.preventDefault();
                handleBack();
              },
            }}
            title={branchStore.details?.nameEn || "New branch"}
          >
            {branchStore.details?.isTestBranch && (
              <CustomBadge variant="info" className="mr-auto my-auto">
                Testing
              </CustomBadge>
            )}
          </HeaderWithBack>
        )}

        <FormNavigation navigationList={navigationList} />
        <div
          key={`${activeId}-${branchStore.details?.id ?? "new"}`}
          className="animate-fade-in"
          style={{ display: "block" }}
        >
          {getStepForm(activeId)}
        </div>

        <CustomCardFooter>
          <CustomButton
            type="button"
            variant="light"
            onClick={handleBack}
            data-testid="multi-step-form-back-button"
          >
            {activeId === FIRST_STEP ? "Cancel" : "Back"}
          </CustomButton>

          <div className="flex gap-4">
            {(activeId === 2 || activeId === 3 || activeId === 4) && (
              <CustomButton
                variant="link"
                type="button"
                onClick={handleSkip}
                data-testid="multi-step-form-skip-button"
              >
                Skip
              </CustomButton>
            )}
            <CustomButton
              type="submit"
              form={idForm}
              isPending={isPending}
              disabled={!stepValidity[activeId]}
              data-testid="multi-step-form-next-button"
            >
              {activeId === LAST_STEP || isSingleStepSave ? "Save" : "Next"}
            </CustomButton>
          </div>
        </CustomCardFooter>
      </FormCardBasic>
    );
  }
);

export default BranchSteps;
